/**
 * Attribute Permissions Management Page
 * 
 * Allows admins to configure per-attribute governance:
 * - Who can define value ranges (e.g., Fibonacci, T-shirt sizes)
 * - Who can approve value ranges
 * - Who can propose values for each attribute
 * - Who can accept/approve values for each attribute
 * - How values are collected (any member, specific person, tool result, etc.)
 * - Evidence requirements
 * 
 * Data is persisted to Supabase via attributeGovernanceStore.
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  Shield, 
  Edit, 
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Check,
  AlertCircle,
  RefreshCw,
  Users,
  UserCheck,
  Wrench,
  FileCheck,
  Settings,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { 
  useAttributeGovernanceStore,
  type AttributeGovernanceTemplate,
  type CollectionMethod,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { VALUES_FROM } from '@/lib/valuesFrom';
import type { Attribute } from '../../../types/prioritization/lifecycle';
import type { CapabilityId } from '../../../types/prioritization/capabilityIds';

// ============================================================================
// Constants — Capabilities for Attribute Governance
// ============================================================================

const GOVERNANCE_CAPABILITIES: { id: CapabilityId; label: string; description: string }[] = [
  { id: 'VALUE_RANGE_DEFINE', label: 'Define Value Ranges', description: 'Can define scales like Fibonacci, T-shirt sizes, Low/Medium/High' },
  { id: 'VALUE_RANGE_APPROVE', label: 'Approve Value Ranges', description: 'Can approve proposed value ranges before values are collected' },
  { id: 'SCALES_DEFINE', label: 'Define Scales', description: 'Can create and edit measurement scales on attributes' },
  { id: 'VALUE_ENTER', label: 'Enter Values', description: 'Can propose/enter values for attributes on items' },
  { id: 'VALUE_ACCEPT', label: 'Accept Values', description: 'Can accept proposed values' },
  { id: 'VALUE_REJECT', label: 'Reject Values', description: 'Can reject proposed values' },
  { id: 'OVERRIDE_APPLY', label: 'Override Values', description: 'Can override accepted values' },
  { id: 'CRITERIA_SELECT', label: 'Select Criteria', description: 'Can select which criteria are used in a cycle' },
];

// The five answers to "where do this criterion's values come from?" — one list,
// one set of words, for this page, the cycle's override and the track's column
// (src/lib/valuesFrom.ts, migration 1062).
const COLLECTION_METHOD_ICONS: Record<CollectionMethod, React.ElementType> = {
  any_group_member: FileCheck,
  specific_person: UserCheck,
  tool_result: Wrench,
  facilitator_entry: Users,
  participant_vote: Users,
};
const COLLECTION_METHODS: { id: CollectionMethod; label: string; description: string; icon: React.ElementType }[] =
  VALUES_FROM.map((a) => ({ id: a.id, label: a.label, description: a.meaning, icon: COLLECTION_METHOD_ICONS[a.id] }));

// ============================================================================
// Sub-components
// ============================================================================

function CapabilitySelector({ 
  selectedCapabilities, 
  onChange,
  label,
  description,
  relevantCapabilities,
}: { 
  selectedCapabilities: string[];
  onChange: (caps: string[]) => void;
  label: string;
  description?: string;
  relevantCapabilities?: CapabilityId[];
}) {
  const caps = relevantCapabilities
    ? GOVERNANCE_CAPABILITIES.filter(c => relevantCapabilities.includes(c.id))
    : GOVERNANCE_CAPABILITIES;

  const toggleCap = (capId: string) => {
    if (selectedCapabilities.includes(capId)) {
      onChange(selectedCapabilities.filter(c => c !== capId));
    } else {
      onChange([...selectedCapabilities, capId]);
    }
  };
  
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      {description && <p className="text-xs text-[var(--text-ter)] mb-2">{description}</p>}
      <div className="flex flex-wrap gap-2">
        {caps.map(cap => (
          <button
            key={cap.id}
            type="button"
            onClick={() => toggleCap(cap.id)}
            title={cap.description}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedCapabilities.includes(cap.id)
                ? 'bg-primary-600 text-white'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {selectedCapabilities.includes(cap.id) && <Check className="inline h-3 w-3 mr-1" />}
            {cap.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CollectionMethodSelector({
  value,
  onChange,
}: {
  value: CollectionMethod;
  onChange: (method: CollectionMethod) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Values come from</label>
      <div className="grid grid-cols-1 gap-2">
        {COLLECTION_METHODS.map(method => {
          const Icon = method.icon;
          const isSelected = value === method.id;
          return (
            <button
              key={method.id}
              type="button"
              onClick={() => onChange(method.id)}
              className={`flex items-start gap-3 p-3  border text-left transition-colors ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
              }`}
            >
              <Icon className={`h-5 w-5 mt-0.5 ${isSelected ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
              <div>
                <div className={`font-medium text-sm ${isSelected ? 'text-primary-900' : 'text-[var(--text-pri)]'}`}>
                  {method.label}
                </div>
                <div className="text-xs text-[var(--text-ter)]">{method.description}</div>
              </div>
              {isSelected && (
                <Check className="h-5 w-5 text-primary-600 ml-auto" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AttributeGovernanceCard({
  attribute,
  template,
  onSave,
  isSaving,
  initialExpanded = false,
  initialEditing = false,
}: {
  attribute: Attribute;
  template?: AttributeGovernanceTemplate;
  onSave: (data: Partial<AttributeGovernanceTemplate>) => Promise<void>;
  isSaving: boolean;
  /** When true, card starts expanded (e.g. standalone governance detail from Attributes). */
  initialExpanded?: boolean;
  /** When true, opens directly in edit mode (implies expanded). */
  initialEditing?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(() => initialExpanded || initialEditing);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [localData, setLocalData] = useState({
    rangeDefinerRoles: template?.rangeDefinerRoles || ['VALUE_RANGE_DEFINE', 'SCALES_DEFINE'],
    rangeApproverRoles: template?.rangeApproverRoles || ['VALUE_RANGE_APPROVE'],
    valueProposerRoles: template?.valueProposerRoles || ['VALUE_ENTER'],
    valueApproverRoles: template?.valueApproverRoles || ['VALUE_ACCEPT'],
    collectionMethod: (template?.collectionMethod || 'any_group_member') as CollectionMethod,
    requiresEvidence: template?.requiresEvidence || false,
  });
  
  // Reset local data when template changes
  useEffect(() => {
    if (template) {
      setLocalData({
        rangeDefinerRoles: template.rangeDefinerRoles,
        rangeApproverRoles: template.rangeApproverRoles,
        valueProposerRoles: template.valueProposerRoles,
        valueApproverRoles: template.valueApproverRoles,
        collectionMethod: template.collectionMethod,
        requiresEvidence: template.requiresEvidence,
      });
    }
  }, [template]);
  
  const handleSave = async () => {
    await onSave({
      attributeKey: attribute.key,
      ...localData,
      scopeKind: 'global',
      isActive: true,
    });
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    // Reset to template values
    if (template) {
      setLocalData({
        rangeDefinerRoles: template.rangeDefinerRoles,
        rangeApproverRoles: template.rangeApproverRoles,
        valueProposerRoles: template.valueProposerRoles,
        valueApproverRoles: template.valueApproverRoles,
        collectionMethod: template.collectionMethod,
        requiresEvidence: template.requiresEvidence,
      });
    }
    setIsEditing(false);
  };
  
  const hasTemplate = !!template;
  const collectionMethodLabel = COLLECTION_METHODS.find(m => m.id === localData.collectionMethod)?.label || 'Unknown';
  
  return (
    <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
      {/* Header */}
      <div 
        className="px-4 py-3 bg-[var(--surface)] flex items-center justify-between cursor-pointer hover:bg-[var(--hover-tint)]"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Shield className={`h-5 w-5 ${hasTemplate ? 'text-primary-500' : 'text-[var(--text-ter)]'}`} />
          <div>
            <h4 className="font-medium text-[var(--text-pri)]">{attribute.label}</h4>
            <p className="text-sm text-[var(--text-ter)]">{attribute.key} - {attribute.type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasTemplate ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
              Configured
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)]">
              Using Defaults
            </span>
          )}
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
            {collectionMethodLabel}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-[var(--text-ter)]" />
          ) : (
            <ChevronDown className="h-5 w-5 text-[var(--text-ter)]" />
          )}
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-[var(--rule)]">
          {!isEditing ? (
            <>
              {/* Read-only view */}
              <div className="grid grid-cols-2 gap-4">
                {/* Range Definition */}
                <div className="space-y-3">
                  <h5 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Range Definition
                  </h5>
                  <div>
                    <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Can Define Ranges</p>
                    <div className="flex flex-wrap gap-1">
                      {localData.rangeDefinerRoles.length > 0 ? (
                        localData.rangeDefinerRoles.map(cap => (
                          <span key={cap} className="px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded text-xs font-medium">
                            {GOVERNANCE_CAPABILITIES.find(c => c.id === cap)?.label || cap}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--text-ter)] italic">Not set</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Can Approve Ranges</p>
                    <div className="flex flex-wrap gap-1">
                      {localData.rangeApproverRoles.length > 0 ? (
                        localData.rangeApproverRoles.map(cap => (
                          <span key={cap} className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs font-medium">
                            {GOVERNANCE_CAPABILITIES.find(c => c.id === cap)?.label || cap}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--text-ter)] italic">Not set</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Value Provision */}
                <div className="space-y-3">
                  <h5 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    Value Provision
                  </h5>
                  <div>
                    <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Can Propose Values</p>
                    <div className="flex flex-wrap gap-1">
                      {localData.valueProposerRoles.length > 0 ? (
                        localData.valueProposerRoles.map(cap => (
                          <span key={cap} className="px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded text-xs font-medium">
                            {GOVERNANCE_CAPABILITIES.find(c => c.id === cap)?.label || cap}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--text-ter)] italic">Any capability</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Can Approve Values</p>
                    <div className="flex flex-wrap gap-1">
                      {localData.valueApproverRoles.length > 0 ? (
                        localData.valueApproverRoles.map(cap => (
                          <span key={cap} className="px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] rounded text-xs font-medium">
                            {GOVERNANCE_CAPABILITIES.find(c => c.id === cap)?.label || cap}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--text-ter)] italic">Any capability</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer info */}
              <div className="flex items-center justify-between pt-3 border-t border-[var(--rule)]">
                <div className="flex items-center gap-4 text-sm text-[var(--text-sec)]">
                  <span className="flex items-center gap-1">
                    <Wrench className="h-4 w-4" />
                    {collectionMethodLabel}
                  </span>
                  {localData.requiresEvidence && (
                    <span className="flex items-center gap-1 text-[var(--mustard)]">
                      <AlertCircle className="h-4 w-4" />
                      Evidence Required
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                  className="btn btn-secondary text-sm flex items-center gap-1"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Edit form */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h5 className="text-sm font-semibold text-[var(--text-pri)]">Range Definition</h5>
                  <CapabilitySelector
                    label="Required capabilities to define value ranges"
                    description="Users with these capabilities can define scales (Fibonacci, T-shirt sizes, etc.)"
                    selectedCapabilities={localData.rangeDefinerRoles}
                    onChange={(caps) => setLocalData(prev => ({ ...prev, rangeDefinerRoles: caps }))}
                    relevantCapabilities={['VALUE_RANGE_DEFINE', 'SCALES_DEFINE']}
                  />
                  <CapabilitySelector
                    label="Required capabilities to approve value ranges"
                    description="Must approve before values can be collected"
                    selectedCapabilities={localData.rangeApproverRoles}
                    onChange={(caps) => setLocalData(prev => ({ ...prev, rangeApproverRoles: caps }))}
                    relevantCapabilities={['VALUE_RANGE_APPROVE']}
                  />
                </div>
                
                <div className="space-y-4">
                  <h5 className="text-sm font-semibold text-[var(--text-pri)]">Value Provision</h5>
                  <CapabilitySelector
                    label="Required capabilities to propose values"
                    selectedCapabilities={localData.valueProposerRoles}
                    onChange={(caps) => setLocalData(prev => ({ ...prev, valueProposerRoles: caps }))}
                    relevantCapabilities={['VALUE_ENTER', 'OVERRIDE_APPLY']}
                  />
                  <CapabilitySelector
                    label="Required capabilities to approve values"
                    selectedCapabilities={localData.valueApproverRoles}
                    onChange={(caps) => setLocalData(prev => ({ ...prev, valueApproverRoles: caps }))}
                    relevantCapabilities={['VALUE_ACCEPT', 'VALUE_REJECT', 'OVERRIDE_APPLY']}
                  />
                </div>
              </div>
              
              <div className="pt-4 border-t border-[var(--rule)]">
                <CollectionMethodSelector
                  value={localData.collectionMethod}
                  onChange={(method) => setLocalData(prev => ({ ...prev, collectionMethod: method }))}
                />
              </div>
              
              <label className="flex items-center gap-2 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  checked={localData.requiresEvidence}
                  onChange={(e) => setLocalData(prev => ({ ...prev, requiresEvidence: e.target.checked }))}
                  className="rounded text-primary-600"
                />
                <span className="text-sm text-[var(--text-sec)]">Require evidence when proposing values</span>
              </label>
              
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary text-sm flex items-center gap-1"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary text-sm flex items-center gap-1"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function AttributePermissions() {
  const { attributes, fetchFromSupabase: fetchAttributes, syncState: attributesSyncState } = useAttributesStore();
  const { 
    templates, 
    fetchFromSupabase: fetchTemplates, 
    createTemplate,
    updateTemplate,
    syncState: governanceSyncState,
  } = useAttributeGovernanceStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [savingAttribute, setSavingAttribute] = useState<string | null>(null);
  
  // Load data on mount
  useEffect(() => {
    fetchAttributes();
    fetchTemplates();
  }, [fetchAttributes, fetchTemplates]);
  
  // Create template map for quick lookup
  const templateMap = useMemo(() => {
    const map = new Map<string, AttributeGovernanceTemplate>();
    templates.forEach(t => {
      if (t.scopeKind === 'global' && t.isActive) {
        map.set(t.attributeKey, t);
      }
    });
    return map;
  }, [templates]);
  
  // Filter attributes
  const filteredAttributes = useMemo(() => {
    return attributes.filter(attr => {
      const matchesSearch = 
        attr.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attr.key.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [attributes, searchTerm]);
  
  const handleSaveTemplate = async (attributeKey: string, data: Partial<AttributeGovernanceTemplate>) => {
    setSavingAttribute(attributeKey);
    try {
      const existingTemplate = templateMap.get(attributeKey);
      
      if (existingTemplate) {
        await updateTemplate(existingTemplate.id, data);
      } else {
        await createTemplate({
          ...data,
          attributeKey,
          rangeDefinerRoles: data.rangeDefinerRoles || [],
          rangeApproverRoles: data.rangeApproverRoles || [],
          valueProposerRoles: data.valueProposerRoles || [],
          valueApproverRoles: data.valueApproverRoles || [],
          collectionMethod: data.collectionMethod || 'any_group_member',
          requiresEvidence: data.requiresEvidence || false,
          evidenceTypes: data.evidenceTypes || [],
          scopeKind: 'global',
          isActive: true,
          createdBy: 'admin',
        });
      }
      
      setNotification({
        type: 'success',
        message: `Governance settings saved for ${attributeKey}`,
      });
    } catch (err) {
      setNotification({
        type: 'error',
        message: `Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setSavingAttribute(null);
      setTimeout(() => setNotification(null), 3000);
    }
  };
  
  // Stats
  const stats = useMemo(() => {
    const configuredCount = templates.filter(t => t.scopeKind === 'global' && t.isActive).length;
    const evidenceRequiredCount = templates.filter(t => t.requiresEvidence && t.isActive).length;
    const toolResultCount = templates.filter(t => t.collectionMethod === 'tool_result' && t.isActive).length;
    
    return {
      total: attributes.length,
      configured: configuredCount,
      evidenceRequired: evidenceRequiredCount,
      toolResult: toolResultCount,
    };
  }, [attributes, templates]);
  
  const isLoading = attributesSyncState.isLoading || governanceSyncState.isLoading;
  
  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Settings className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Attribute Governance"
        subtitle="Configure which capabilities are required to define ranges, propose values, and approve values for each attribute"
        actions={
          <button
            onClick={() => { fetchAttributes(); fetchTemplates(); }}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            disabled={isLoading}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />
      
      {/* Notification */}
      {notification && (
        <div className={`p-4  flex items-center gap-3 ${
          notification.type === 'success' 
            ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]' 
            : 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]'
        }`}>
          {notification.type === 'success' ? (
            <Check className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {notification.message}
        </div>
      )}
      
      {/* Info Card */}
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
          <div>
            <h3 className="font-medium text-[var(--text-sec)]">Capability-Based Attribute Governance</h3>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              Configure which capabilities are required for each attribute governance action. Users must have the
              assigned capabilities to define value ranges, propose values, or approve them. Capabilities are
              granted to users via role assignments in Settings &rarr; Roles. Cycles can override these defaults.
            </p>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-2xl font-bold text-[var(--text-pri)]">{stats.total}</div>
          <div className="text-sm text-[var(--text-sec)]">Total Attributes</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{stats.configured}</div>
          <div className="text-sm text-[var(--text-sec)]">Configured</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-2xl font-bold text-[var(--mustard)]">{stats.evidenceRequired}</div>
          <div className="text-sm text-[var(--text-sec)]">Require Evidence</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-2xl font-bold text-[var(--text-ter)]">{stats.toolResult}</div>
          <div className="text-sm text-[var(--text-sec)]">Use Tool Results</div>
        </div>
      </div>
      
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search attributes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm"
          />
          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
        </div>
      </div>
      
      {/* Attribute List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-[var(--text-ter)]">
            <RefreshCw className="h-8 w-8 mx-auto text-[var(--text-ter)] mb-3 animate-spin" />
            <p>Loading...</p>
          </div>
        ) : filteredAttributes.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-ter)]">
            <Shield className="h-12 w-12 mx-auto text-[var(--text-ter)] mb-3" />
            <p>No attributes found</p>
          </div>
        ) : (
          filteredAttributes.map(attr => (
            <AttributeGovernanceCard
              key={attr.key}
              attribute={attr}
              template={templateMap.get(attr.key)}
              onSave={(data) => handleSaveTemplate(attr.key, data)}
              isSaving={savingAttribute === attr.key}
            />
          ))
        )}
      </div>
    </div>
  );
}
